/**
 * Panel CSS Styles
 *
 * Scoped CSS for the Data Notebook panel, injected into Shadow DOM.
 * Uses CSS variables for theming support.
 */

export const panelStyles = `
/* ===== CSS Variables ===== */
:host {
  /* Dark theme colors (default) */
  --dn-bg-primary: #1a1a2e;
  --dn-bg-secondary: #16213e;
  --dn-bg-tertiary: #0f3460;
  --dn-bg-input: #0d1b2a;

  --dn-text-primary: #eaeaea;
  --dn-text-secondary: #a0a0a0;
  --dn-text-muted: #6c6c6c;

  --dn-border-color: #2d3748;
  --dn-border-focus: #4a9eff;

  --dn-accent: #4a9eff;
  --dn-accent-hover: #3182ce;
  --dn-success: #48bb78;
  --dn-warning: #ed8936;
  --dn-error: #fc8181;

  --dn-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  --dn-shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.3);

  --dn-radius: 8px;
  --dn-radius-sm: 4px;

  --dn-font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  --dn-font-mono: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;

  --dn-panel-width: 500px;
  --dn-panel-height: 400px;
  --dn-header-height: 40px;
  --dn-tabs-height: 36px;
  --dn-status-height: 28px;

  --dn-transition: 0.2s ease;
}

/* Light theme */
:host(.light) {
  --dn-bg-primary: #ffffff;
  --dn-bg-secondary: #f7fafc;
  --dn-bg-tertiary: #edf2f7;
  --dn-bg-input: #ffffff;

  --dn-text-primary: #1a202c;
  --dn-text-secondary: #4a5568;
  --dn-text-muted: #a0aec0;

  --dn-border-color: #e2e8f0;
  --dn-border-focus: #3182ce;

  --dn-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  --dn-shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* ===== Reset ===== */
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ===== Panel Container ===== */
.dn-panel {
  position: fixed;
  width: var(--dn-panel-width);
  height: var(--dn-panel-height);
  background: var(--dn-bg-primary);
  border: 1px solid var(--dn-border-color);
  border-radius: var(--dn-radius);
  box-shadow: var(--dn-shadow);
  font-family: var(--dn-font-family);
  font-size: 13px;
  color: var(--dn-text-primary);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
  transition: opacity var(--dn-transition), transform var(--dn-transition);
  z-index: 2147483647;
}

/* Position variants */
.dn-panel.bottom-right {
  bottom: 20px;
  right: 20px;
}

.dn-panel.bottom-left {
  bottom: 20px;
  left: 20px;
}

.dn-panel.top-right {
  top: 20px;
  right: 20px;
}

.dn-panel.top-left {
  top: 20px;
  left: 20px;
}

/* Hidden state */
.dn-panel.hidden {
  opacity: 0;
  pointer-events: none;
  transform: scale(0.95) translateY(10px);
}

/* ===== Header ===== */
.dn-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--dn-header-height);
  padding: 0 12px;
  background: var(--dn-bg-secondary);
  border-bottom: 1px solid var(--dn-border-color);
  cursor: move;
  user-select: none;
}

.dn-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 13px;
}

.dn-header-icon {
  font-size: 16px;
}

.dn-header-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.dn-header-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: var(--dn-radius-sm);
  color: var(--dn-text-secondary);
  cursor: pointer;
  font-size: 14px;
  transition: background var(--dn-transition), color var(--dn-transition);
}

.dn-header-btn:hover {
  background: var(--dn-bg-tertiary);
  color: var(--dn-text-primary);
}

.dn-header-btn.close:hover {
  background: var(--dn-error);
  color: white;
}

/* ===== Tabs ===== */
.dn-tabs {
  display: flex;
  align-items: center;
  height: var(--dn-tabs-height);
  padding: 0 8px;
  background: var(--dn-bg-secondary);
  border-bottom: 1px solid var(--dn-border-color);
  gap: 4px;
}

.dn-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: var(--dn-radius-sm);
  color: var(--dn-text-secondary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background var(--dn-transition), color var(--dn-transition);
}

.dn-tab:hover {
  background: var(--dn-bg-tertiary);
  color: var(--dn-text-primary);
}

.dn-tab.active {
  background: var(--dn-accent);
  color: white;
}

.dn-tab-icon {
  font-size: 12px;
}

/* ===== Content Area ===== */
.dn-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dn-tab-content {
  display: none;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
}

.dn-tab-content.active {
  display: flex;
}

/* ===== SQL Tab ===== */
.dn-sql-input-container {
  padding: 12px;
  border-bottom: 1px solid var(--dn-border-color);
}

.dn-sql-input {
  width: 100%;
  min-height: 80px;
  padding: 10px;
  background: var(--dn-bg-input);
  border: 1px solid var(--dn-border-color);
  border-radius: var(--dn-radius-sm);
  color: var(--dn-text-primary);
  font-family: var(--dn-font-mono);
  font-size: 12px;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition: border-color var(--dn-transition);
}

.dn-sql-input:focus {
  border-color: var(--dn-border-focus);
}

.dn-sql-input::placeholder {
  color: var(--dn-text-muted);
}

.dn-sql-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.dn-run-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--dn-accent);
  border: none;
  border-radius: var(--dn-radius-sm);
  color: white;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background var(--dn-transition);
}

.dn-run-btn:hover {
  background: var(--dn-accent-hover);
}

.dn-run-btn:disabled {
  background: var(--dn-text-muted);
  cursor: not-allowed;
}

.dn-shortcut-hint {
  color: var(--dn-text-muted);
  font-size: 11px;
}

/* ===== Results Area ===== */
.dn-results {
  flex: 1;
  overflow: auto;
  padding: 12px;
}

.dn-results-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--dn-text-muted);
  text-align: center;
}

.dn-results-placeholder-icon {
  font-size: 32px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.dn-results-placeholder-text {
  font-size: 12px;
}

/* Results error state */
.dn-results-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--dn-error);
  text-align: center;
  padding: 20px;
}

.dn-results-error-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.dn-results-error-text {
  font-size: 12px;
  max-width: 400px;
  word-break: break-word;
}

/* Results success state */
.dn-results-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--dn-success);
  text-align: center;
}

.dn-results-success-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.dn-results-success-text {
  font-size: 12px;
}

/* Results table wrapper */
.dn-results-table-wrapper {
  overflow: auto;
  max-height: 100%;
}

.dn-results-row-count {
  padding: 8px 0;
  font-size: 11px;
  color: var(--dn-text-muted);
}

/* NULL value styling */
.dn-null {
  color: var(--dn-text-muted);
  font-style: italic;
}

/* Results table */
.dn-results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.dn-results-table th,
.dn-results-table td {
  padding: 6px 10px;
  text-align: left;
  border-bottom: 1px solid var(--dn-border-color);
}

.dn-results-table th {
  background: var(--dn-bg-secondary);
  font-weight: 600;
  color: var(--dn-text-secondary);
  position: sticky;
  top: 0;
}

.dn-results-table tr:hover td {
  background: var(--dn-bg-secondary);
}

/* ===== Datasets Tab ===== */
.dn-datasets {
  flex: 1;
  overflow: auto;
  padding: 12px;
}

.dn-datasets-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--dn-text-muted);
  text-align: center;
}

.dn-datasets-empty-icon {
  font-size: 32px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.dn-datasets-empty-text {
  font-size: 12px;
  margin-bottom: 4px;
}

.dn-datasets-empty-hint {
  font-size: 11px;
  color: var(--dn-text-muted);
}

.dn-dataset-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--dn-bg-secondary);
  border: 1px solid var(--dn-border-color);
  border-radius: var(--dn-radius-sm);
  margin-bottom: 8px;
}

.dn-dataset-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dn-dataset-name {
  font-weight: 500;
  font-family: var(--dn-font-mono);
  font-size: 12px;
}

.dn-dataset-meta {
  font-size: 11px;
  color: var(--dn-text-muted);
}

/* ===== Status Bar ===== */
.dn-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--dn-status-height);
  padding: 0 12px;
  background: var(--dn-bg-secondary);
  border-top: 1px solid var(--dn-border-color);
  font-size: 11px;
  color: var(--dn-text-muted);
}

.dn-status-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dn-status-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--dn-success);
}

.dn-status-indicator.loading {
  background: var(--dn-warning);
  animation: pulse 1s infinite;
}

.dn-status-indicator.error {
  background: var(--dn-error);
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* ===== Minimized Badge ===== */
.dn-badge {
  position: fixed;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--dn-bg-primary);
  border: 1px solid var(--dn-border-color);
  border-radius: var(--dn-radius);
  box-shadow: var(--dn-shadow-sm);
  font-family: var(--dn-font-family);
  font-size: 13px;
  color: var(--dn-text-primary);
  cursor: pointer;
  pointer-events: auto;
  transition: transform var(--dn-transition), box-shadow var(--dn-transition);
  z-index: 2147483647;
}

.dn-badge:hover {
  transform: scale(1.02);
  box-shadow: var(--dn-shadow);
}

.dn-badge.hidden {
  display: none;
}

.dn-badge.bottom-right {
  bottom: 20px;
  right: 20px;
}

.dn-badge.bottom-left {
  bottom: 20px;
  left: 20px;
}

.dn-badge.top-right {
  top: 20px;
  right: 20px;
}

.dn-badge.top-left {
  top: 20px;
  left: 20px;
}

.dn-badge-icon {
  font-size: 16px;
}

.dn-badge-count {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  background: var(--dn-accent);
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  color: white;
}

/* ===== REPL Tab ===== */
.dn-repl-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  min-height: 200px;
  background: #0d1117;
}

/* XTerm shell container styles */
.dn-repl-container .xterm {
  height: 100%;
  width: 100%;
  padding: 8px;
}

.dn-repl-container .xterm-viewport {
  overflow-y: auto !important;
  background-color: #0d1117 !important;
}

.dn-repl-container .xterm-screen {
  height: 100%;
}

/* Ensure terminal canvas is visible */
.dn-repl-container canvas {
  display: block !important;
}

/* Force xterm text colors to be visible */
.dn-repl-container .xterm-rows {
  color: #e6e6e6 !important;
}

.dn-repl-container .xterm-cursor-layer {
  z-index: 10;
}

/* xterm.js color classes - ensure they're visible in shadow DOM */
.xterm-fg-1 { color: #cc0000 !important; }
.xterm-fg-2 { color: #4e9a06 !important; }
.xterm-fg-3 { color: #c4a000 !important; }
.xterm-fg-4 { color: #3465a4 !important; }
.xterm-fg-5 { color: #75507b !important; }
.xterm-fg-6 { color: #06989a !important; }
.xterm-fg-7 { color: #d3d7cf !important; }
.xterm-bg-1 { background-color: #cc0000 !important; }
.xterm-bg-2 { background-color: #4e9a06 !important; }
.xterm-bg-3 { background-color: #c4a000 !important; }
.xterm-bg-4 { background-color: #3465a4 !important; }
.xterm-bg-5 { background-color: #75507b !important; }
.xterm-bg-6 { background-color: #06989a !important; }
.xterm-bg-7 { background-color: #d3d7cf !important; }

.dn-repl {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0d1117;
  font-family: var(--dn-font-mono);
  font-size: 12px;
  overflow: hidden;
}

.dn-repl-output {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  line-height: 1.5;
}

.dn-repl-welcome {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--dn-border-color);
}

.dn-repl-logo {
  font-size: 18px;
  font-weight: 700;
  color: #f0c040;
  margin-bottom: 4px;
}

.dn-repl-version {
  font-size: 11px;
  color: var(--dn-text-muted);
  margin-bottom: 8px;
}

.dn-repl-hint {
  font-size: 11px;
  color: var(--dn-text-secondary);
}

.dn-repl-line {
  margin-bottom: 4px;
  white-space: pre-wrap;
  word-break: break-word;
}

.dn-repl-command {
  color: #58a6ff;
}

.dn-repl-result {
  color: var(--dn-text-secondary);
}

.dn-repl-error {
  color: var(--dn-error);
}

.dn-repl-success {
  color: var(--dn-success);
}

.dn-repl-input-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #161b22;
  border-top: 1px solid var(--dn-border-color);
  gap: 8px;
}

.dn-repl-prompt {
  color: #f0c040;
  font-weight: 600;
  flex-shrink: 0;
}

.dn-repl-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--dn-text-primary);
  font-family: inherit;
  font-size: inherit;
  padding: 0;
}

.dn-repl-input::placeholder {
  color: var(--dn-text-muted);
}

/* REPL Table */
.dn-repl-table-wrapper {
  margin: 8px 0;
  overflow-x: auto;
}

.dn-repl-table {
  border-collapse: collapse;
  font-size: 11px;
  min-width: 100%;
}

.dn-repl-table th,
.dn-repl-table td {
  padding: 4px 12px;
  text-align: left;
  border: 1px solid var(--dn-border-color);
  white-space: nowrap;
}

.dn-repl-table th {
  background: #21262d;
  color: var(--dn-text-secondary);
  font-weight: 600;
}

.dn-repl-table td {
  background: #0d1117;
  color: var(--dn-text-primary);
}

.dn-repl-table tr:hover td {
  background: #161b22;
}

.dn-repl-row-count {
  margin-top: 4px;
  font-size: 10px;
  color: var(--dn-text-muted);
}

/* Loading state */
.dn-repl-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 12px;
  color: var(--dn-text-muted);
}

.dn-repl-loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--dn-border-color);
  border-top-color: var(--dn-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.dn-repl-loading-text {
  font-size: 12px;
}
`;
